<!-- /* * Confidential and Proprietary. * Do not distribute without 1-800-Flowers.com, Inc. consent. *
Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved. */ -->

<div
	class="button-wrapper {shouldDisplayFloraButtonEntry ? '' : 'button-wrapper-hidden-mobile'}"
	data-testid="button-wrapper"
	in:fade
>
	<ChatPreview {toggleChat} />
	<button
		class="chat-button {vibrate ? 'vibrating' : ''}"
		data-testid="chat-button"
		on:click={() => {
			chatSession.update((cs) => ({ ...cs, entry_point: 'global' }));
			updateSessionStorage({ ...$chatSession, entry_point: 'global' });
			toggleChat();
		}}
	>
		<img src="floraChatJuly2025.svg" alt="img" class="icon-image" />
		<span class="button-text">Chat</span>
	</button>
</div>

<script lang="ts">
	import { fade } from 'svelte/transition';
	import {
		chatHeaderButtonClicked,
		chatPreviewMsgVisible,
		chatSession,
		entryMessage,
		isChatSessionReady,
		reInitializeChat,
		updateSessionStorage,
		chatTriggeredExternally,
		chatVisible,
		persistentSessionData
	} from '$lib/stores/chatSessionStore';
	import { env } from '$env/dynamic/public';
	import ChatPreview from './ChatPreview.svelte';
	import { datadogLogs } from '@datadog/browser-logs';

	export let initChat: () => Promise<void>;
	export let restartConversation: () => Promise<void>;
	let vibrate = false;
	let previewDisplayed = false;
	let noPreviewDisplayed = false;
	let shouldDisplayFloraButtonEntry = false;
	$: {
		if (
			$chatSession?.deviceType &&
			$chatSession?.deviceType?.toLowerCase() !== 'mobile' &&
			$chatSession?.operatingSystem?.toLowerCase() !== 'android'
		) {
			shouldDisplayFloraButtonEntry = true;
		} else {
			if (
				$persistentSessionData?.allowedDeviceTypes?.includes(
					$persistentSessionData?.operatingSystem?.toLowerCase()
				)
			) {
				const conversationStarted =
					!$reInitializeChat && $chatSession?.conversation_id && !shouldDisplayFloraButtonEntry;
				const conversationEnded =
					($reInitializeChat || !$chatSession?.conversation_id) && shouldDisplayFloraButtonEntry;
				if (conversationStarted) shouldDisplayFloraButtonEntry = true;
				if (conversationEnded) shouldDisplayFloraButtonEntry = false;
			}
		}
	}
	$: if ($entryMessage && $entryMessage.suggestion !== '') {
		toggleChat();
	}
	$: if ($chatTriggeredExternally && !$chatVisible) {
		toggleChat();
		chatTriggeredExternally.set(false);
	}
	$: if ($chatHeaderButtonClicked) {
		toggleChat();
		chatHeaderButtonClicked.set(false);
	}
	$: if ($chatSession.messages && $chatSession.flags?.['chat-shake-animation-enabled']) {
		vibrate = true;
		setTimeout(() => {
			vibrate = false;
		}, 500);
	}
	$: {
		if (
			!previewDisplayed &&
			$chatSession.flags?.['chat-show-preview-enabled'] &&
			$chatPreviewMsgVisible
		) {
			window.parent.postMessage({ type: 'flora-preview-msg-ready' }, '*');
			previewDisplayed = true;
		}

		if (
			!noPreviewDisplayed &&
			(!$chatSession.flags?.['chat-show-preview-enabled'] || !$chatPreviewMsgVisible)
		) {
			window.parent.postMessage({ type: 'flora-no-preview-msg' }, '*');
			noPreviewDisplayed = true;
		}
	}
	export const toggleChat = async () => {
		window.parent.postMessage({ type: 'flora-get-page-info' }, '*');
		if ($reInitializeChat) {
			await restartConversation();
		} else {
			if (env.PUBLIC_NODE_ENV === 'dev' && env.PUBLIC_MOCK_STORE_JSON) {
				let mock = {};
				try {
					mock = JSON.parse(env.PUBLIC_MOCK_STORE_JSON as string);
				} catch {}
				chatSession.update((old) => {
					return { ...old, ...mock };
				});
			}
			window.parent.postMessage({ type: 'flora-conv-ui-session-info' }, '*');
			chatSession.update((old) => {
				return { ...old, token: '' };
			});
		}
		const unsubscribe = isChatSessionReady.subscribe(async (ready) => {
			if (ready) {
				unsubscribe();
				await initChat();
			}
		});
		// post message to mbp-pwa-ui
		window.parent.postMessage(
			{
				type: 'flora-chat-iframe-maximised'
			},
			'*'
		);
	};
</script>

<style>
	.button-wrapper {
		width: 103px;
		height: 48px;
		margin: 8px;
		display: inline-flex;
		overflow: hidden;
		justify-content: center;
		align-items: center;
		border-radius: 999px;
		background: #fafafa;
		box-shadow: 0px 0px 6px 0px rgba(0, 0, 0, 0.25);
	}
	.button-wrapper-hidden-mobile {
		display: none;
		pointer-events: none;
	}
	.chat-button {
		cursor: pointer;
		overflow: hidden;
		display: flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		border: none;
		background: #fafafa;
	}
	.icon-image {
		width: 40px;
		height: 40px;
		width: 29px;
		height: 32px;
		aspect-ratio: 29/32;
	}
	.button-text {
		color: #555;
		font-family: 'Area Variable';
		font-size: 16px;
		font-style: normal;
		font-weight: 700;
		line-height: 118.75%;
		letter-spacing: 0.16px;
	}

	@keyframes vibrate {
		0% {
			transform: translateX(0);
		}
		25% {
			transform: translateX(-2px);
		}
		50% {
			transform: translateX(2px);
		}
		75% {
			transform: translateX(-2px);
		}
		100% {
			transform: translateX(0);
		}
	}

	.vibrating {
		animation: vibrate 0.5s ease-in-out;
	}
</style>
